import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { useToast } from '../components/Toast';
import type { Institution, StaffLogin, StaffRole } from '../types/models';

export function StaffLoginPage() {
  const toast = useToast();
  const { orgId = '' } = useParams();

  const [insts, setInsts] = useState<Institution[]>([]);
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [institutionId, setInstitutionId] = useState('');
  const [staffRoleId, setStaffRoleId] = useState('');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState<StaffLogin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Grid filters — narrow the loaded rows by institution / staff role (independent of the
  // "Generate Login" controls above).
  const [gridInst, setGridInst] = useState('');
  const [gridRole, setGridRole] = useState('');

  useEffect(() => {
    Promise.all([api.listInstitutions(orgId), api.listStaffRoles(orgId)])
      .then(([i, r]) => { setInsts(i); setRoles(r); })
      .catch(e => setError(e?.message ?? 'Failed to load filters.'));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function load() {
    setError(null);
    try {
      setRows(await api.listStaffLogins(orgId, institutionId || undefined, staffRoleId || undefined, search || undefined));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load staff logins.');
    }
  }

  async function generate() {
    setError(null); setMsg(null);
    if (!staffRoleId) { setError('Select a staff role to generate logins for.'); return; }
    setBusy(true);
    try {
      const r = await api.generateLogins(orgId, staffRoleId, institutionId || undefined);
      setMsg(`Generated ${r.created} login(s). ${r.alreadyExisted} already had logins. (${r.totalStaff} staff in this role.)`);
      toast.success(`${r.created} login(s) generated.`);
      await load();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to generate logins.';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword(row: StaffLogin) {
    if (!confirm(`Reset ${row.name}'s password back to the default? They'll be asked to change it next login.`)) return;
    setError(null); setMsg(null);
    try {
      await api.resetStaffLoginPassword(row.loginId);
      setMsg(`Password for ${row.name} reset to the default (12345678).`);
      toast.success('Password reset.');
      await load();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to reset password.';
      setError(msg);
      toast.error(msg);
    }
  }

  async function toggleActive(row: StaffLogin) {
    setError(null); setMsg(null);
    try {
      await api.setStaffLoginActive(row.loginId, !row.isActive);
      toast.success('Login status updated.');
      await load();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to update status.';
      setError(msg);
      toast.error(msg);
    }
  }

  // Grid filter options, derived from the loaded rows so we only offer values that exist.
  const instOptions = [...new Set((rows ?? []).map(r => r.institutionName).filter(Boolean))].sort();
  const roleOptions = [...new Set((rows ?? []).map(r => r.loginType).filter(Boolean))].sort();
  const visibleRows = (rows ?? []).filter(r =>
    (!gridInst || r.institutionName === gridInst)
    && (!gridRole || r.loginType === gridRole));

  return (
    <OrgLayout>
      <h1 style={{ marginTop: 0 }}>Staff Login</h1>

      <div className="card">
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Institution</div>
            <select value={institutionId} onChange={e => setInstitutionId(e.target.value)}>
              <option value="">All institutions</option>
              {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Staff Role *</div>
            <select value={staffRoleId} onChange={e => setStaffRoleId(e.target.value)}>
              <option value="">Select…</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.code} — {r.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Code or Name</div>
            <input value={search} placeholder="Code / Name / Login"
                   onChange={e => setSearch(e.target.value)} />
          </label>
          <button onClick={load}>Search</button>
          <button className="primary" onClick={generate} disabled={busy || !staffRoleId}>
            {busy ? 'Generating…' : 'Generate Login'}
          </button>
        </div>
        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          “Generate Login” creates logins for every staff member under the selected role
          (default password <strong>12345678</strong>, changed on first login). Staff that already
          have a login are skipped.
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {msg && <div className="card" style={{ padding: '8px 16px' }}><span className="muted">{msg}</span></div>}

      {rows && rows.length > 0 && (
        <div className="card row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Institution</div>
            <select value={gridInst} onChange={e => setGridInst(e.target.value)}>
              <option value="">All institutions</option>
              {instOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Staff Role</div>
            <select value={gridRole} onChange={e => setGridRole(e.target.value)}>
              <option value="">All roles</option>
              {roleOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          {(gridInst || gridRole) && (
            <button type="button" onClick={() => { setGridInst(''); setGridRole(''); }}>Clear</button>
          )}
          <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>
            Showing {visibleRows.length} of {rows.length}
          </span>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Staff Code</th>
              <th>Name</th>
              <th>Login Name</th>
              <th>Login Type</th>
              <th>Status</th>
              <th>Last Login</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={8} className="muted">Loading…</td></tr>}
            {rows && rows.length === 0 && (
              <tr><td colSpan={8} className="muted">No staff logins yet. Pick a role and click “Generate Login”.</td></tr>
            )}
            {rows && rows.length > 0 && visibleRows.length === 0 && (
              <tr><td colSpan={8} className="muted">No staff logins match the selected filters.</td></tr>
            )}
            {visibleRows.map((r, idx) => (
              <tr key={r.loginId}>
                <td>{idx + 1}</td>
                <td className="slug">{r.staffCode}</td>
                <td style={{ fontWeight: 600 }}>{r.name}</td>
                <td>{r.loginName}</td>
                <td>{r.loginType}</td>
                <td>
                  <span className="badge" style={!r.isActive ? { background: 'var(--danger-soft-bg)', color: 'var(--danger-soft-fg)' } : undefined}>
                    {r.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {r.passwordChangeNextLogin && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· must change pw</span>}
                </td>
                <td>{r.lastSuccessfulLoginDt ? new Date(r.lastSuccessfulLoginDt).toLocaleString() : '—'}</td>
                <td>
                  <button className="link-btn" onClick={() => resetPassword(r)}>Reset pw</button>
                  <button className="link-btn" onClick={() => toggleActive(r)}>
                    {r.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </OrgLayout>
  );
}
